/* The New draft / Edit project form, ported from renderProjectForm (app.ts
   ~4092), collectForm (~4127), and saveProject (~4162). Identity only (N34):
   name, directory, charge code. What the project NEEDS is Resourcing's subject
   matter and is edited there; the span is recorded on the Charter (RC15), so
   this form points there instead of carrying the two date inputs.

   The REST calls are byte-identical to the vanilla client: POST /api/projects
   for a create, PUT /api/projects/:id for an edit, with the same JSON body.
   The store is frozen for the projects bundle, so the save writes state through
   `store.update` and lands with the store's navigation actions. */
import { useRef, type JSX } from "react";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { directoryPath } from "../../../shared/directories";
import {
  validateProjectInput,
  type Project,
  type ProjectInput,
} from "../../../shared/projects";
import { SAVE_FAILED, failureReason, savedNotRedrawn } from "./projectsHttp";

export function ProjectFormView(): JSX.Element {
  const { state, store } = useAppState();
  const editing = state.projects.find((p) => p.id === state.editingId);

  const nameRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLSelectElement>(null);
  const chargeRef = useRef<HTMLInputElement>(null);
  const chargeUnconfirmedRef = useRef<HTMLInputElement>(null);

  /* undefined = the form carried no select (dirsError) — the payload omits the
     field and the server keeps the current filing (DIR8) */
  const chosenDirectory = (): string | null | undefined => {
    const select = dirRef.current;
    return select ? select.value || null : undefined;
  };

  /* `editing` — not the presence of a field in the DOM — says whether this is a
     create, so this reads the same value saveProject's POST-vs-PUT test does. */
  const collectForm = (): ProjectInput => {
    const name = (nameRef.current as HTMLInputElement).value.trim();
    const directoryId = chosenDirectory();
    /* the create form has no detail fields to read (N33) */
    if (!editing) return { name, roles: [], chargeCode: null, directoryId };
    const code = (chargeRef.current as HTMLInputElement).value.trim();
    return {
      name,
      /* Edit never touches the needs — they are edited on Resourcing (N34) */
      roles: editing.roles,
      directoryId,
      /* blank = honestly not recorded (charge-codes C1) */
      chargeCode: code
        ? { code, unconfirmed: (chargeUnconfirmedRef.current as HTMLInputElement).checked }
        : null,
    };
  };

  /* Put the saved record into state and go wherever the save should land you —
     the ONE create-vs-edit decision saveProject resolved (N23). */
  const landSavedProject = (wasEdit: boolean, saved: Project): void => {
    store.update({
      projects: wasEdit
        ? state.projects.map((p) => (p.id === saved.id ? saved : p))
        : [...state.projects, saved],
    });
    if (wasEdit) {
      /* Edit is only reachable from the open record, so it returns to it */
      store.selectNav("projects");
      store.openProject(saved.id);
      return;
    }
    /* a new project is a draft, and New draft opens it (N23) */
    store.openProject(saved.id);
  };

  const saveProject = async (): Promise<void> => {
    if (state.saving) return;
    const input = collectForm();
    /* shared validation for instant feedback — the server re-checks (D8) */
    const reason = validateProjectInput(input, state.roles);
    if (reason) {
      store.update({ formError: reason });
      return;
    }
    store.update({ saving: true });
    /* The try covers the REQUEST only. Everything past it runs with a record
       that already exists, so a throw there must never be reported as a failed
       save (OH10). */
    let saved: Project;
    try {
      const res = await fetch(editing ? `/api/projects/${editing.id}` : "/api/projects", {
        method: editing ? "PUT" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        store.update({ saving: false, formError: await failureReason(res, SAVE_FAILED) });
        return;
      }
      saved = (await res.json()) as Project;
    } catch {
      store.update({ saving: false, formError: SAVE_FAILED });
      return;
    }
    store.update({ saving: false });
    try {
      landSavedProject(editing !== undefined, saved);
    } catch {
      store.update({ formError: savedNotRedrawn(`"${saved.name}"`) });
    }
  };

  /* the Directory select both record forms carry (DIR5): Not filed plus every
     folder of the record's own section, named by its path (DIR9 fail-safe). */
  const chosen = editing ? editing.directoryId : state.selectedDirId;
  const dirOptions = state.directories
    .filter((d) => d.section === "projects")
    .map((d) => ({ id: d.id, path: directoryPath(d.id, state.directories) }))
    .sort((a, b) => a.path.localeCompare(b.path));

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>{editing ? "Edit project" : "New draft project"}.</b>
          <span>
            {editing
              ? "Name it and mark the roles it needs — people are allocated later, from Teams."
              : "Name it. What it needs, and everything else, you record in the draft it opens."}
          </span>
        </div>
      }
      footnote={
        editing
          ? "Roles come from the canonical list — never free text."
          : "A new project starts as a draft, and opens so you can fill it in."
      }
    >
      <div className="project-form">
        <label className="field">
          <span className="field-label">Project name</span>
          <input
            id="project-name"
            ref={nameRef}
            type="text"
            defaultValue={editing?.name ?? ""}
            placeholder="e.g. Scythe Technology Integration"
          />
        </label>
        {state.dirsError ? (
          <p className="field dirs-error">
            <span className="field-label">Directory</span>
            not recorded — the folders couldn't load, so this save leaves the filing as it is
          </p>
        ) : (
          <label className="field">
            <span className="field-label">Directory</span>
            <select id="record-dir" ref={dirRef} defaultValue={chosen ?? ""}>
              <option value="">Not filed</option>
              {dirOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.path}
                </option>
              ))}
            </select>
          </label>
        )}
        {editing ? (
          <>
            <label className="field">
              <span className="field-label">Charge code (SAP) — leave blank if not known</span>
              <input
                id="charge-code"
                ref={chargeRef}
                type="text"
                defaultValue={editing.chargeCode?.code ?? ""}
                placeholder="e.g. K-4512-77"
              />
            </label>
            <label className="charge-unconfirmed-row">
              <input
                id="charge-unconfirmed"
                ref={chargeUnconfirmedRef}
                type="checkbox"
                defaultChecked={editing.chargeCode?.unconfirmed ?? false}
              />
              <span>Unconfirmed — an assumption, not verified yet</span>
            </label>
            <div className="slot-note span-pointer">
              <b>Starts (month)</b> and <b>Ends (month)</b> are recorded on the Charter — opening it
              leaves this form, so save first.
              <button
                type="button"
                className="open-charter btn link small"
                onClick={() => {
                  store.openProject(editing.id);
                  store.openArea("Charter");
                }}
              >
                Open the Charter
              </button>
            </div>
          </>
        ) : null}
        <p id="form-error" className="form-error" role="alert">
          {state.formError ?? ""}
        </p>
        <div className="form-actions">
          <button id="save-project" className="btn primary" onClick={() => void saveProject()}>
            Save project
          </button>
          <button
            id="cancel-project"
            className="btn ghost"
            onClick={() => store.selectNav("projects")}
          >
            Cancel
          </button>
        </div>
      </div>
    </ViewFrame>
  );
}
